import React, {useEffect, useState} from 'react';
import {Platform, useWindowDimensions} from 'react-native';
import {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {RFValue} from 'react-native-responsive-fontsize';

import {TAB_HEIGHT} from '../../../routes/components/FloatingTabBar/styles';
import {
  Card,
  CardIcon,
  CardText,
  IconCircle,
  ProgressFill,
  ProgressTrack,
  Wrapper,
} from './styles';

const TOAST_DURATION_MS = 5000;
const GAP_ABOVE_TAB_BAR = RFValue(14);

const SPRING_IN = {damping: 22, stiffness: 200, mass: 0.8};
const EASE_IN = Easing.bezier(0.4, 0, 0.6, 1);

interface Props {
  visible: boolean;
  onTap: () => void;
  onAutoDismiss: () => void;
}

export function FinesReminderToast({visible, onTap, onAutoDismiss}: Props) {
  const insets = useSafeAreaInsets();
  const {height: SCREEN_HEIGHT} = useWindowDimensions();

  const [mounted, setMounted] = useState(false);
  const translateY = useSharedValue(SCREEN_HEIGHT);
  const progress = useSharedValue(1);

  // Espelha o cálculo de tabBarBottom do FloatingTabBar (routes/components/FloatingTabBar/index.tsx)
  // — o toast é montado na raiz do app, fora da árvore da tab bar, então não dá pra
  // reaproveitar o valor diretamente. Se a fórmula da tab bar mudar, atualizar aqui também.
  const hasThreeButtonNavigation = Platform.OS === 'android' && insets.bottom >= 32;
  const tabBarBottom = (insets.bottom > 0 ? insets.bottom : 10) + (hasThreeButtonNavigation ? 10 : 0);
  const bottomOffset = tabBarBottom + TAB_HEIGHT + GAP_ABOVE_TAB_BAR;

  useEffect(() => {
    if (visible) {
      translateY.value = SCREEN_HEIGHT;
      progress.value = 1;
      setMounted(true);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible && mounted) {
      translateY.value = withTiming(SCREEN_HEIGHT, {duration: 280, easing: EASE_IN}, finished => {
        if (finished) {
          runOnJS(setMounted)(false);
        }
      });
    }
  }, [visible]);

  useEffect(() => {
    if (!mounted) {return;}

    translateY.value = withSpring(0, SPRING_IN);
    progress.value = withTiming(0, {duration: TOAST_DURATION_MS, easing: Easing.linear});

    const timer = setTimeout(onAutoDismiss, TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [mounted]);

  const containerStyle = useAnimatedStyle(() => ({
    transform: [{translateY: translateY.value}],
  }));

  const progressStyle = useAnimatedStyle(() => ({
    width: `${progress.value * 100}%`,
  }));

  if (!mounted) {return null;}

  return (
    <Wrapper pointerEvents="box-none" bottomOffset={bottomOffset} style={containerStyle}>
      <Card
        activeOpacity={0.9}
        onPress={onTap}
        accessibilityRole="button"
        accessibilityLabel="Parcele multas e débitos veiculares. Toque para saber mais.">
        <IconCircle>
          <CardIcon name="car" size={RFValue(18)} />
        </IconCircle>
        <CardText numberOfLines={2}>Parcele multas e débitos do seu veículo</CardText>
        <ProgressTrack>
          <ProgressFill style={progressStyle} />
        </ProgressTrack>
      </Card>
    </Wrapper>
  );
}
